import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, Subscription, Subscriber, EMPTY, defer, identity } from 'rxjs';
import { mergeMap, tap, catchError, takeUntil, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { Query } from '../../models/analytics-api/query.model';
import { QueryResult } from '../../models/analytics-api/query_result.model';
import { AnalyticsApiService } from '../../services/analytics-api/analytics-api.service';
import { RequestOptionsArgs } from '../backend/backend.service';
import { MAX_CONCURRENT_QUERIES } from '../../variables';

@Injectable({
  providedIn: 'root',
})
export class WidgetQueueProxyService implements OnDestroy {
  private queryQueue$: Subject<{
    query: Query;
    options: RequestOptionsArgs;
    subscriber: Subscriber<QueryResult>;
    abandoned$: Subject<void>;
  }>;

  private cancelRequests$ = new Subject<void>();
  private processingSubscription: Subscription;
  private queueSize$ = new Subject<number>();
  private queueSizeSubscription: Subscription;
  private activeQueriesCount = 0;
  private outstandingQueriesCount = 0;
  private queueEmpty$ = new Subject<void>();

  constructor(
    private analyticsApi: AnalyticsApiService,
    @Inject(MAX_CONCURRENT_QUERIES) private maxConcurrentQueries: number,
  ) {
    this.initProcessingSubscription();
    this.initQueueSizeTracking();
  }

  private initProcessingSubscription() {
    this.queryQueue$ = new Subject<{
      query: Query;
      options: RequestOptionsArgs;
      subscriber: Subscriber<QueryResult>;
      abandoned$: Subject<void>;
    }>();

    this.processingSubscription = this.queryQueue$
      .pipe(
        takeUntil(this.cancelRequests$),
        mergeMap(
          ({ query, options, subscriber, abandoned$ }) => {
            if (subscriber.closed) {
              return EMPTY;
            }
            this.activeQueriesCount++;
            this.queueSize$.next(this.activeQueriesCount);
            return this.analyticsApi.runQuery(query, options).pipe(
              takeUntil(this.cancelRequests$),
              options.cache ? identity : takeUntil(abandoned$),
              tap({
                next: (result) => subscriber.next(result),
                error: (error) => subscriber.error(error),
                complete: () => subscriber.complete(),
              }),
              catchError(() => {
                return EMPTY;
              }),
              finalize(() => {
                this.activeQueriesCount--;
                this.queueSize$.next(this.activeQueriesCount);
              }),
            );
          },
          this.maxConcurrentQueries,
        ),
      )
      .subscribe();
  }

  private initQueueSizeTracking() {
    this.queueSizeSubscription = this.queueSize$.pipe(
      distinctUntilChanged(),
      debounceTime(5000),
    ).subscribe(size => {
      if (size === 0) {
        this.queueEmpty$.next();
      }
    });
  }

  analiticsQueueProxy(
    query: Query,
    options: RequestOptionsArgs,
  ): Observable<QueryResult> {
    return new Observable<QueryResult>((subscriber) => {
      this.outstandingQueriesCount++;
      const abandoned$ = new Subject<void>();
      this.queryQueue$.next({ query, options, subscriber, abandoned$ });
      return () => {
        this.outstandingQueriesCount--;
        abandoned$.next();
        abandoned$.complete();
      };
    }).pipe(
      takeUntil(this.cancelRequests$),
    );
  }

  public trackRequest<T>(request: Observable<T>): Observable<T> {
    return defer(() => {
      this.activeQueriesCount++;
      this.queueSize$.next(this.activeQueriesCount);
      return request.pipe(
        takeUntil(this.cancelRequests$),
        finalize(() => {
          this.activeQueriesCount--;
          this.queueSize$.next(this.activeQueriesCount);
        }),
      );
    });
  }

  public get busy(): boolean {
    return this.outstandingQueriesCount > 0;
  }

  public onQueueEmpty(): Observable<void> {
    this.queueSize$.next(this.activeQueriesCount);
    return this.queueEmpty$.asObservable();
  }

  public cancelQueryQueue() {
    if (this.processingSubscription) {
      this.processingSubscription.unsubscribe();
    }

    this.cancelRequests$.next();
    this.cancelRequests$ = new Subject<void>();
    this.activeQueriesCount = 0;
    this.queueSize$.next(this.activeQueriesCount);
    this.initProcessingSubscription();
  }

  ngOnDestroy() {
    this.processingSubscription.unsubscribe();
    this.queueSizeSubscription.unsubscribe();
    this.cancelRequests$.next();
    this.cancelRequests$.complete();
    this.queryQueue$.complete();
    this.queueSize$.complete();
    this.queueEmpty$.complete();
  }
}
